import { Fragment, useEffect, useMemo, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { HomeNoticeBlocks } from "../components/HomeNoticeBlocks";
import type { MonthBundle, PublicEvent } from "../types/public";
import type { PublicNoticesBundle } from "../types/site-notices";
import { groupByEventDate } from "../lib/event-sort";
import { buildMonthCalendarCells, shiftYearMonth } from "../lib/month-calendar-grid";
import { computeDayPopupStyle, measureCalDayCell, type DayPopupAnchor } from "../lib/day-popup-geometry";
import { normalizeMonthBundle } from "../lib/normalize-public-bundle";
import { getVisitorSubmitterHash, getVisitorSubmitterKey } from "../lib/visitor-submitter";
import { EventBadgeStamp } from "../components/EventBadgeStamp";
import { KarinCalendarStamp, KarinCalendarStampLegend } from "../components/KarinCalendarStamp";
import { PublicDayOthersSection } from "../components/PublicDayOthersSection";
import { PublicEventShareRow } from "../components/PublicEventShareRow";
import { useVisitorEventRequestModals } from "../hooks/useVisitorEventRequestModals";
import { SubmitEventForm } from "./SubmitEventPage";
import {
  HOME_QUICK_GROUP_FILTERS,
  HOME_QUICK_PERSON_LABELS,
  matchesHomeSearch,
} from "../lib/home-quick-search";
import {
  KARIN_SUB_LABEL,
  formatDoorCurtainSlots,
  formatTimeRange,
  getPublicEventBadge,
  isOnlineKarinStampEvent,
  jstCurrentYearMonth,
  jstTodayYmd,
  parseHpSlotsFromMemo,
  previewPerformanceOneLine,
  publicPerformersList,
  remoteKindLabel,
  weekdayJp,
} from "../types/public";

type KarinFilter = "all" | "mini_live" | "withlive" | "limista" | "other";
type MapPopupAnchor = { prefecture: string; top: number; left: number; width: number; height: number };
type MapPopupEventGroup = { key: string; main: PublicEvent; events: PublicEvent[] };

const PREFECTURE_MAP_POS: Record<string, { x: number; y: number }> = {
  北海道: { x: 75, y: 17 },
  青森: { x: 64, y: 28 },
  岩手: { x: 66, y: 36 },
  宮城: { x: 65, y: 46 },
  秋田: { x: 61, y: 35 },
  山形: { x: 61, y: 46 },
  福島: { x: 61, y: 55 },
  茨城: { x: 63, y: 64 },
  栃木: { x: 59, y: 61 },
  群馬: { x: 55, y: 62 },
  埼玉: { x: 58, y: 67 },
  千葉: { x: 64, y: 70 },
  東京: { x: 59, y: 70 },
  神奈川: { x: 57, y: 74 },
  新潟: { x: 52, y: 49 },
  富山: { x: 43, y: 58 },
  石川: { x: 40, y: 56 },
  福井: { x: 38, y: 64 },
  山梨: { x: 53, y: 71 },
  長野: { x: 50, y: 65 },
  岐阜: { x: 45, y: 69 },
  静岡: { x: 52, y: 77 },
  愛知: { x: 47, y: 75 },
  三重: { x: 44, y: 79 },
  滋賀: { x: 42, y: 71 },
  京都: { x: 38, y: 71 },
  大阪: { x: 36, y: 77 },
  兵庫: { x: 31, y: 74 },
  奈良: { x: 39, y: 77 },
  和歌山: { x: 37, y: 83 },
  鳥取: { x: 26, y: 68 },
  島根: { x: 21, y: 70 },
  岡山: { x: 29, y: 74 },
  広島: { x: 23, y: 75 },
  山口: { x: 17, y: 78 },
  徳島: { x: 34, y: 85 },
  香川: { x: 31, y: 81 },
  愛媛: { x: 24, y: 85 },
  高知: { x: 29, y: 89 },
  福岡: { x: 14, y: 83 },
  佐賀: { x: 11, y: 85 },
  長崎: { x: 8, y: 89 },
  熊本: { x: 13, y: 91 },
  大分: { x: 18, y: 88 },
  宮崎: { x: 17, y: 95 },
  鹿児島: { x: 12, y: 97 },
  沖縄: { x: 37, y: 92 },
};

const PREFECTURE_NAMES = Object.keys(PREFECTURE_MAP_POS).sort((a, b) => b.length - a.length);

function normalizePrefectureName(raw: string | null | undefined): string | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const noSuffix = s.replace(/[都道府県]$/, "");
  if (PREFECTURE_MAP_POS[noSuffix]) return noSuffix;
  for (const p of PREFECTURE_NAMES) {
    if (s.includes(p) || s.includes(`${p}県`) || s.includes(`${p}府`) || s.includes(`${p}都`) || s.includes(`${p}道`)) {
      return p;
    }
  }
  return null;
}

function eventMapPrefecture(e: PublicEvent): string | null {
  return normalizePrefectureName(e.prefecture) ?? normalizePrefectureName(e.venue) ?? normalizePrefectureName(e.event_name);
}

function groupMapPopupEvents(events: PublicEvent[]): MapPopupEventGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, PublicEvent[]>();
  for (const e of events) {
    const key = [e.event_date, e.event_name.trim(), String(e.venue ?? "").trim()].join("|");
    if (!byKey.has(key)) {
      byKey.set(key, []);
      order.push(key);
    }
    byKey.get(key)!.push(e);
  }
  return order.map((key) => {
    const grouped = byKey.get(key)!;
    return { key, main: grouped[0]!, events: grouped };
  });
}

function computeMapPopupDocumentStyle(a: MapPopupAnchor): CSSProperties {
  const margin = 8;
  const vw = typeof window !== "undefined" ? window.innerWidth : 400;
  const sx = typeof window !== "undefined" ? window.scrollX : 0;
  const sy = typeof window !== "undefined" ? window.scrollY : 0;
  const popW = Math.min(340, vw - margin * 2);
  let left = sx + a.left + a.width / 2 - popW / 2;
  left = Math.max(sx + margin, Math.min(left, sx + vw - popW - margin));
  return {
    position: "absolute",
    top: sy + a.top + a.height + 8,
    left,
    width: popW,
    maxHeight: "none",
    zIndex: 10050,
    margin: 0,
  };
}

function monthOptions(): string[] {
  const ym = jstCurrentYearMonth();
  const [y0, m0] = ym.split("-").map(Number);
  const out: string[] = [];
  for (let i = -6; i <= 18; i++) {
    const u = Date.UTC(y0, m0 - 1 + i, 1);
    const yy = new Date(u).getUTCFullYear();
    const mm = String(new Date(u).getUTCMonth() + 1).padStart(2, "0");
    out.push(`${yy}-${mm}`);
  }
  return out;
}

function matchesKarinSubtype(e: PublicEvent, f: KarinFilter): boolean {
  if (!e.is_karin) return true;
  if (f === "all") return true;
  const sub = e.karin_subtype ?? "other";
  return sub === f;
}

/** 一覧：当月は「今日」以降だけ。未来月・過去月は全日（日付フォーカス時も他の日は消さない） */
function karinPassesListDateFloor(e: PublicEvent, monthLoaded: string): boolean {
  const curYm = jstCurrentYearMonth();
  if (monthLoaded < curYm) return true;
  if (monthLoaded > curYm) return true;
  return e.event_date >= jstTodayYmd();
}

/** カレンダー印：同じイベント名なら1つだけ（指定アイコンがあれば優先、配信系が混ざればメ優先） */
function karinStampGroupsForDay(karin: PublicEvent[]): { titleKey: string; representative: PublicEvent; isRemote: boolean; icon: string | null }[] {
  const m = new Map<string, { representative: PublicEvent; remote: boolean; icon: string | null }>();
  for (const e of karin) {
    const titleKey = String(e.event_name ?? "").trim() || e.id;
    const prev = m.get(titleKey);
    const remote = isOnlineKarinStampEvent(e);
    const icon = e.karin_icon?.trim() || null;
    if (!prev) m.set(titleKey, { representative: e, remote, icon });
    else m.set(titleKey, { representative: prev.representative, remote: prev.remote || remote, icon: prev.icon ?? icon });
  }
  return [...m.entries()].map(([titleKey, v]) => ({ titleKey, representative: v.representative, isRemote: v.remote, icon: v.icon }));
}

function venueDisplayLine(e: PublicEvent): string {
  const pref = String(e.prefecture ?? "").trim();
  const v = String(e.venue ?? "").trim();
  if (pref && v) return `${pref} · ${v}`;
  if (v) return v;
  if (pref) return pref;
  return "会場未定";
}

/** 折りたたみ時に見せる時間・開場開演の一行 */
function slotTimeSummaryLine(e: PublicEvent): string {
  const tr = formatTimeRange(e);
  const slots = formatDoorCurtainSlots(e);
  const bits: string[] = [];
  if (tr && tr !== "—") bits.push(`開始〜終了 ${tr}`);
  if (slots) bits.push(slots);
  return bits.join(" ｜ ") || "時間は未定または未入力です";
}

const ALL_MONTHS_VALUE = "all";

export function HomePage() {
  const months = useMemo(() => monthOptions(), []);
  const defaultMonth = jstCurrentYearMonth();
  const [month, setMonth] = useState(() => (months.includes(defaultMonth) ? defaultMonth : months[6]!));
  const calendarMonth = month === ALL_MONTHS_VALUE ? defaultMonth : month;
  const [bundle, setBundle] = useState<MonthBundle | null>(null);
  const [allKarinEvents, setAllKarinEvents] = useState<PublicEvent[]>([]);
  const [notices, setNotices] = useState<PublicNoticesBundle | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [karinFilter, setKarinFilter] = useState<KarinFilter>("all");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [submitInitialDate, setSubmitInitialDate] = useState("");
  const [visitorSubmitterHash, setVisitorSubmitterHash] = useState<string | null>(null);
  /** カレンダーの印タップ後、一覧の該当ブロックへスクロール */
  const [scrollIntent, setScrollIntent] = useState<string | null>(null);
  const [monthScrollIntent, setMonthScrollIntent] = useState<string | null>(null);
  /** 「他の現場を詳しく」の開閉（カレンダーの色丸タップで開く） */
  const [othersDetailsOpen, setOthersDetailsOpen] = useState<Record<string, boolean>>({});
  /** 日付タップで開く当日プレビュー（クリックしたマス位置に固定表示） */
  const [dayPopup, setDayPopup] = useState<DayPopupAnchor | null>(null);
  /** 地図の顔アイコンタップで開く都道府県プレビュー */
  const [mapPopup, setMapPopup] = useState<MapPopupAnchor | null>(null);
  const { openCorrectionModal, visitorModalEl } = useVisitorEventRequestModals();

  useEffect(() => {
    let alive = true;
    void getVisitorSubmitterHash().then((hash) => {
      if (alive) setVisitorSubmitterHash(hash);
    });
    return () => {
      alive = false;
    };
  }, []);

  /** カレンダーの色丸タップで「他の現場を詳しく」を開く（details は非制御。open 制御は React 18 で不整合になりやすい） */
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      for (const [day, want] of Object.entries(othersDetailsOpen)) {
        if (!want) continue;
        const el = document.getElementById(`home-others-details-${day}`);
        if (el instanceof HTMLDetailsElement) el.open = true;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [othersDetailsOpen]);

  useEffect(() => {
    if (month !== ALL_MONTHS_VALUE && selectedDate && selectedDate.slice(0, 7) !== month) {
      setSelectedDate(null);
    }
  }, [month, selectedDate]);

  useEffect(() => {
    setScrollIntent(null);
    setDayPopup(null);
    setMapPopup(null);
  }, [month]);

  useEffect(() => {
    if (!dayPopup && !mapPopup) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        setDayPopup(null);
        setMapPopup(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [dayPopup, mapPopup]);

  useEffect(() => {
    if (!dayPopup) return;
    const sync = () => {
      const next = measureCalDayCell(dayPopup.day, "#home-public-month-calendar");
      if (next) setDayPopup(next);
    };
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [dayPopup?.day]);

  useEffect(() => {
    if (!scrollIntent || selectedDate !== scrollIntent) return;
    const day = scrollIntent;
    setScrollIntent(null);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(`home-schedule-${day}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }, [scrollIntent, selectedDate]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/public-data/notices.json", { cache: "no-store" });
        if (res.ok) {
          const data = (await res.json()) as PublicNoticesBundle;
          if (alive) setNotices(data);
        }
      } catch {
        /* お知らせは任意 */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/public-data/karin.json", { cache: "no-store" });
        if (!res.ok) return;
        const raw = (await res.json()) as { events?: PublicEvent[] };
        if (alive) setAllKarinEvents((raw.events ?? []).filter((e) => e.is_karin));
      } catch {
        /* 月データがあるので一覧だけフォールバック */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await fetch(`/public-data/${calendarMonth}.json`, { cache: "no-store" });
        if (!res.ok) throw new Error(`読み込みに失敗しました (${res.status})`);
        const raw = await res.json();
        const data = normalizeMonthBundle(raw);
        if (!data) throw new Error("月データの形式が不正です");
        if (alive) setBundle(data);
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [calendarMonth]);

  const filtered = useMemo(() => {
    if (!bundle) return [];
    return bundle.events.filter((e) => matchesHomeSearch(e, q) && matchesKarinSubtype(e, karinFilter));
  }, [bundle, q, karinFilter]);

  const karinEventDateSet = useMemo(() => {
    const dates = new Set<string>();
    if (!bundle) return dates;
    for (const [day, dayData] of Object.entries(bundle.byDay)) {
      if ((dayData.karin ?? []).length > 0) dates.add(day);
    }
    return dates;
  }, [bundle]);

  const selectedDayOthers = useMemo(() => {
    if (!bundle || !selectedDate) return [];
    return (bundle.byDay[selectedDate]?.others ?? []).filter((e) => matchesHomeSearch(e, q));
  }, [bundle, selectedDate, q]);

  const karinForList = useMemo(() => {
    const src = allKarinEvents.length ? allKarinEvents : filtered.filter((e) => e.is_karin);
    return src
      .filter((e) => matchesHomeSearch(e, q) && matchesKarinSubtype(e, karinFilter))
      .filter((e) => {
        if (month === ALL_MONTHS_VALUE) return e.event_date >= jstTodayYmd();
        if (!e.event_date.startsWith(`${month}-`)) return false;
        if (month === jstCurrentYearMonth()) return e.event_date >= jstTodayYmd();
        return true;
      })
      .sort((a, b) => `${a.event_date} ${a.curtain_time ?? a.time_start ?? ""}`.localeCompare(`${b.event_date} ${b.curtain_time ?? b.time_start ?? ""}`));
  }, [allKarinEvents, filtered, q, karinFilter, month]);

  const otherSearchResults = useMemo(() => {
    if (!bundle || !q.trim()) return [];
    return bundle.events
      .filter((e) => !e.is_karin && matchesHomeSearch(e, q))
      .sort((a, b) => `${a.event_date} ${a.curtain_time ?? a.time_start ?? ""}`.localeCompare(`${b.event_date} ${b.curtain_time ?? b.time_start ?? ""}`));
  }, [bundle, q]);

  const karinForMap = useMemo(() => {
    const src = allKarinEvents.length ? allKarinEvents : filtered.filter((e) => e.is_karin);
    return src
      .filter((e) => (month === ALL_MONTHS_VALUE ? true : e.event_date.startsWith(`${month}-`)))
      .filter((e) => matchesKarinSubtype(e, karinFilter))
      .filter((e) => matchesHomeSearch(e, q));
  }, [allKarinEvents, filtered, month, karinFilter, q]);

  const karinMapGroups = useMemo(() => {
    const byPref = new Map<string, PublicEvent[]>();
    for (const e of karinForMap) {
      const pref = eventMapPrefecture(e);
      if (!pref || !PREFECTURE_MAP_POS[pref]) continue;
      const list = byPref.get(pref) ?? [];
      list.push(e);
      byPref.set(pref, list);
    }
    return [...byPref.entries()]
      .map(([prefecture, events]) => ({
        prefecture,
        events: [...events].sort((a, b) => `${a.event_date} ${a.curtain_time ?? a.time_start ?? ""}`.localeCompare(`${b.event_date} ${b.curtain_time ?? b.time_start ?? ""}`)),
        pos: PREFECTURE_MAP_POS[prefecture]!,
      }))
      .sort((a, b) => a.events[0]!.event_date.localeCompare(b.events[0]!.event_date));
  }, [karinForMap]);

  const mapPopupEvents = useMemo(() => {
    if (!mapPopup) return [];
    return karinMapGroups.find((g) => g.prefecture === mapPopup.prefecture)?.events ?? [];
  }, [karinMapGroups, mapPopup]);

  const mapPopupEventGroups = useMemo(() => groupMapPopupEvents(mapPopupEvents), [mapPopupEvents]);

  const karinByDate = useMemo(() => groupByEventDate(karinForList), [karinForList]);

  const karinCount = useMemo(() => karinByDate.reduce((n, g) => n + g.items.length, 0), [karinByDate]);

  useEffect(() => {
    if (!monthScrollIntent || !karinByDate.length) return;
    if (monthScrollIntent === ALL_MONTHS_VALUE) return;
    const target = karinByDate.find((g) => g.dateKey.startsWith(`${monthScrollIntent}-`));
    if (!target) return;
    setMonthScrollIntent(null);
    requestAnimationFrame(() => {
      document.getElementById(`home-schedule-${target.dateKey}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [monthScrollIntent, karinByDate]);

  const cal = useMemo(() => buildMonthCalendarCells(calendarMonth), [calendarMonth]);

  const listCaption =
    month === ALL_MONTHS_VALUE
      ? "全てを表示・今日以降（日本時間）"
      : month === jstCurrentYearMonth()
      ? `${month}・今日以降（日本時間）`
      : `${month}・全日`;

  const selectedDayKarinCount = useMemo(() => {
    if (!bundle || !selectedDate) return 0;
    return (bundle.byDay[selectedDate]?.karin ?? [])
      .filter((e) => matchesKarinSubtype(e, karinFilter))
      .filter((e) => matchesHomeSearch(e, q)).length;
  }, [bundle, selectedDate, karinFilter, q]);

  const popupKarin = useMemo(() => {
    if (!bundle || !dayPopup) return [];
    return (bundle.byDay[dayPopup.day]?.karin ?? [])
      .filter((e) => matchesKarinSubtype(e, karinFilter))
      .filter((e) => matchesHomeSearch(e, q));
  }, [bundle, dayPopup, karinFilter, q]);

  const popupOthers = useMemo(() => {
    if (!bundle || !dayPopup) return [];
    return (bundle.byDay[dayPopup.day]?.others ?? []).filter((e) => matchesHomeSearch(e, q));
  }, [bundle, dayPopup, q]);

  const openDetailFromPopup = (dayKey: string) => {
    setDayPopup(null);
    setSelectedDate(dayKey);
    setScrollIntent(dayKey);
  };

  const canDeleteFanPost = (e: PublicEvent) =>
    e.source === "fan_post" && Boolean(visitorSubmitterHash && e.fan_submitter_key_hash && e.fan_submitter_key_hash === visitorSubmitterHash);

  const removeEventFromCurrentMonth = (eventId: string) => {
    setBundle((prev) => {
      if (!prev) return prev;
      const byDay = Object.fromEntries(
        Object.entries(prev.byDay).map(([day, group]) => [
          day,
          {
            karin: group.karin.filter((e) => e.id !== eventId),
            others: group.others.filter((e) => e.id !== eventId),
          },
        ]),
      );
      return {
        ...prev,
        events: prev.events.filter((e) => e.id !== eventId),
        byDay,
      };
    });
  };

  const deleteOwnFanPost = async (e: PublicEvent) => {
    if (!canDeleteFanPost(e)) return;
    if (!window.confirm("この投稿を削除しますか？投稿した端末からだけ削除できます。")) return;
    try {
      const res = await fetch(`/api/public/events/${encodeURIComponent(e.id)}`, {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ submitterKey: getVisitorSubmitterKey() }),
      });
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(j.error ?? `HTTP ${res.status}`);
      removeEventFromCurrentMonth(e.id);
      setMapPopup(null);
      setDayPopup(null);
      setErr("投稿を削除しました。カレンダー反映に少し時間がかかることがあります。");
    } catch (error) {
      setErr(`削除できませんでした: ${String(error)}`);
    }
  };

  const scrollToSubmitForm = (eventDate = "") => {
    if (eventDate) setSubmitInitialDate(eventDate);
    setDayPopup(null);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById("home-submit-event-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  };

  const toggleCalendarDayPopup = (dayKey: string, cellEl: HTMLElement) => {
    const r = cellEl.getBoundingClientRect();
    setDayPopup((prev) => (prev?.day === dayKey ? null : { day: dayKey, top: r.top, left: r.left, width: r.width, height: r.height }));
  };

  const toggleMapPrefecturePopup = (prefecture: string, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    setDayPopup(null);
    setMapPopup((prev) =>
      prev?.prefecture === prefecture ? null : { prefecture, top: r.top, left: r.left, width: r.width, height: r.height },
    );
  };

  const renderKarinDayGroups = () =>
    karinByDate.map((g) => {
              const sameDayOthers = (bundle?.byDay?.[g.dateKey]?.others ?? []).filter((e) => matchesHomeSearch(e, q));
              const isFocusedDay = selectedDate === g.dateKey;
              const schedule = (
                <section
                  id={`home-schedule-${g.dateKey}`}
                  className={`venue-group${isFocusedDay ? " venue-group--focused" : ""}`}
                  aria-labelledby={`dh-${g.dateKey}`}
                >
    <article className="event karin karin-hero karin-day-bundle" aria-labelledby={`dh-${g.dateKey}`}>
      <h2 id={`dh-${g.dateKey}`} className="karin-day-bundle-heading">
        {g.heading}
        <span className="karin-day-bundle-count"> ・ この日は{g.items.length}公演</span>
      </h2>
      {g.items.map((e, idx) => {
        const doorSlots = formatDoorCurtainSlots(e);
        const perfFull = String(e.performance_text ?? "").trim();
        const perfOne = previewPerformanceOneLine(e.performance_text, 220);
        return (
          <div key={e.id} className={idx > 0 ? "karin-day-slot karin-day-slot--sep" : "karin-day-slot"}>
            <div className="karin-slot-main">
              <h3 className="title karin-slot-title">{e.event_name}</h3>
              <div className="karin-slot-venue-large">{venueDisplayLine(e)}</div>
              <div className="karin-slot-times-collapsed">
                <span className="karin-slot-times-label">時間</span>
                <span className="karin-slot-times-text">{slotTimeSummaryLine(e)}</span>
              </div>
              <div className="karin-slot-action">
                {e.detail_url ? (
                  <a className="btn small secondary" href={e.detail_url} target="_blank" rel="noreferrer">
                    詳細サイト
                  </a>
                ) : (
                  <span className="pill karin-slot-pill">これから公開だよ！</span>
                )}
              </div>
            </div>
            <details className="karin-slot-details">
              <summary>開く</summary>
              <div className="karin-slot-details-body">
                {remoteKindLabel(e.event_kind) ? (
                  <p className="karin-detail-line">
                    <span className="karin-detail-k">種別</span> {remoteKindLabel(e.event_kind)}
                  </p>
                ) : null}
                {e.karin_subtype ? (
                  <p className="karin-detail-line">
                    <span className="karin-detail-k">タイプ</span> {KARIN_SUB_LABEL[e.karin_subtype] ?? "その他"}
                  </p>
                ) : null}
                {e.prefecture ? (
                  <p className="karin-detail-line">
                    <span className="karin-detail-k">地域</span> {e.prefecture}
                  </p>
                ) : null}
                {doorSlots ? (
                  <p className="karin-detail-line">
                    <span className="karin-detail-k">開場・開演</span> {doorSlots}
                  </p>
                ) : null}
                {perfFull ? (
                  <p className="karin-detail-block">
                    <span className="karin-detail-k">本番・メモ</span>
                    <span className="karin-detail-pre">{perfFull}</span>
                  </p>
                ) : perfOne ? (
                  <p className="karin-detail-line">
                    <span className="karin-detail-k">本番・メモ</span> {perfOne}
                  </p>
                ) : null}
                {e.reserve_url ? (
                  <p className="karin-detail-actions">
                    <a className="btn small" href={e.reserve_url} target="_blank" rel="noreferrer">
                      予約サイト
                    </a>
                  </p>
                ) : null}
              </div>
            </details>
            <PublicEventShareRow event={e} />
            <div className="home-slot-request-row">
              <button type="button" className="btn small secondary" onClick={() => openCorrectionModal(e)}>
                間違ってるよ！
              </button>
            </div>
          </div>
        );
      })}
      {sameDayOthers.length > 0 ? (
        <div className="same-day-other-block">
          <div className="same-day-other-label">この日の他の現場（かぶり確認）</div>
          <div className="same-day-other-stamps" aria-label="他グループ・出演の予定（アイコン）">
            {sameDayOthers.map((o, oi) => (
              <EventBadgeStamp
                key={String(o.id || `${g.dateKey}-stamp-${oi}`)}
                badge={getPublicEventBadge(o)}
                size="sm"
                title={`${o.event_name}（${o.venue ?? "会場"}）`}
              />
            ))}
          </div>
          <details
            className="same-day-others-details"
            id={`home-others-details-${g.dateKey}`}
            onToggle={(ev) => {
              const next = ev.currentTarget.open;
              setOthersDetailsOpen((p) => {
                const prev = p[g.dateKey];
                if (prev === next) return p;
                return { ...p, [g.dateKey]: next };
              });
            }}
          >
            <summary>詳細を開く／閉じる</summary>
            <ul className="same-day-others-list">
              {sameDayOthers.map((o, oi) => {
                const slots = parseHpSlotsFromMemo(o.memo);
                const badge = getPublicEventBadge(o);
                const perfOther = publicPerformersList(o);
                return (
                  <li key={String(o.id || `${g.dateKey}-other-${oi}`)} className="same-day-others-li home-other-li-stack">
                    <div className="same-day-others-li-inner">
                      <EventBadgeStamp badge={badge} size="sm" />
                      <span className="same-day-others-text">
                        {slots != null ? <>{slots}公演・</> : null}
                        {o.event_name}
                        {" ・"}
                        {perfOther.length ? perfOther.join("・") : "—"}
                        {o.detail_url ? (
                          <>
                            {" ・"}
                            <a href={o.detail_url} target="_blank" rel="noreferrer" title="公式の詳細ページ">
                              リンク
                            </a>
                          </>
                        ) : null}
                      </span>
                    </div>
                    <div className="home-other-action-row">
                      <PublicEventShareRow event={o} />
                      {canDeleteFanPost(o) ? (
                        <button type="button" className="btn small secondary" onClick={() => void deleteOwnFanPost(o)}>
                          自分の投稿を削除
                        </button>
                      ) : null}
                      <button type="button" className="btn small secondary" onClick={() => openCorrectionModal(o)}>
                        間違ってるよ！
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </details>
        </div>
      ) : null}
      <div className="notice">申込み・詳細は必ずリンク先の公式情報をご確認ください。</div>
    </article>
                </section>
              );
      return <Fragment key={g.dateKey}>{schedule}</Fragment>;
    });

  const homeSearchAssistBlock = (
    <section className="card home-search-assist-card" aria-label="検索と追加依頼">
      <div className="home-search-block">
        <div className="row home-search-row">
          <input className="input" placeholder="文字・日付・時間で検索（イベント名 / 出演者 / 会場 など）" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="row home-search-time-chips">
          <label className="home-time-picker-label">
            <span className="home-chip-label">時間で探す</span>
            <select className="input home-time-picker-select" value={q.match(/^\d{2}:\d{2}$/) ? q : ""} onChange={(e) => setQ(e.target.value)}>
              <option value="">時間を選択</option>
              {["10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"].map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="row home-chip-label-row">
        <div className="home-chip-label">出演者ですぐ探す（検索に反映）</div>
      </div>
      <div className="row home-chips" style={{ marginBottom: 6 }}>
        {HOME_QUICK_PERSON_LABELS.map((name) => (
          <button
            key={name}
            type="button"
            className={`chip${q.trim() === name ? " active" : ""}`}
            onClick={() => setQ((prev) => (prev.trim() === name ? "" : name))}
          >
            {name}
          </button>
        ))}
      </div>
      <div className="row home-chip-label-row" style={{ marginTop: 4 }}>
        <div className="home-chip-label">グループですぐ探す</div>
      </div>
      <div className="row home-chips">
        {HOME_QUICK_GROUP_FILTERS.map((g) => (
          <button
            key={g.groupCode}
            type="button"
            className={`chip${q.trim() === g.label ? " active" : ""}`}
            onClick={() => setQ((prev) => (prev.trim() === g.label ? "" : g.label))}
          >
            {g.label}
          </button>
        ))}
        <button type="button" className="chip" onClick={() => setQ("")}>
          検索クリア
        </button>
      </div>
      {q.trim() ? (
        <div className="home-search-others-result">
          <div className="home-search-others-head">
            <h2 className="home-search-others-title">他グループの予定</h2>
            <span className="mono home-search-others-count">{otherSearchResults.length}件</span>
          </div>
          {otherSearchResults.length === 0 ? (
            <p className="meta" style={{ margin: 0 }}>
              該当する他グループの予定はありません
            </p>
          ) : (
            <PublicDayOthersSection
              embedded
              events={otherSearchResults}
              onCorrectionRequest={openCorrectionModal}
              canDeleteEvent={canDeleteFanPost}
              onDeleteRequest={(e) => void deleteOwnFanPost(e)}
            />
          )}
        </div>
      ) : null}
    </section>
  );

  const renderMapPopupBody = () => (
    <div className="home-day-pop-body">
      <section className="home-day-modal-section">
        {mapPopupEventGroups.length === 0 ? (
          <p className="home-day-modal-empty">予定はありません</p>
        ) : (
          <ul className="home-day-modal-list">
            {mapPopupEventGroups.map(({ key, main: e, events }) => {
              const overlapOthers = (bundle?.byDay[e.event_date]?.others ?? []).filter((o) => matchesHomeSearch(o, q));
              return (
                <li key={key} className="home-day-modal-li home-map-pop-karin-li">
                  <div className="home-map-pop-karin-head">
                  <KarinCalendarStamp icon={e.karin_icon} isRemote={isOnlineKarinStampEvent(e)} title="宮本佳林の予定" />
                    <div className="home-map-pop-karin-main">
                      <span className="home-map-pop-karin-label">宮本佳林の予定</span>
                      <span className="home-day-modal-li-title home-map-pop-karin-title">{e.event_name}</span>
                    </div>
                  </div>
                  <span className="home-day-pop-venue">
                    {e.event_date}（{weekdayJp(e.event_date)}） ・ {venueDisplayLine(e)}
                  </span>
                  <div className="home-map-pop-performances">
                    {events.length > 1 ? <span className="home-map-pop-performance-count">この日は{events.length}公演</span> : null}
                    {events.map((slot, idx) => (
                      <span key={slot.id} className="home-map-pop-performance-line">
                        {events.length > 1 ? `${idx + 1}公演目: ` : ""}
                        {slotTimeSummaryLine(slot)}
                      </span>
                    ))}
                  </div>
                  {overlapOthers.length > 0 ? (
                    <div className="home-map-pop-overlaps">
                      <div className="home-map-pop-overlaps-title">この日の他の現場</div>
                      {overlapOthers.slice(0, 3).map((o, oi) => (
                        <div key={String(o.id || `${e.id}-map-overlap-${oi}`)} className="home-map-pop-overlap-row">
                          <EventBadgeStamp badge={getPublicEventBadge(o)} size="sm" title={`${o.event_name}（${o.venue ?? "会場"}）`} />
                          <span className="home-map-pop-overlap-text">
                            {o.event_name} ・ {venueDisplayLine(o)}
                          </span>
                        </div>
                      ))}
                      {overlapOthers.length > 3 ? <div className="home-map-pop-overlap-more">ほか {overlapOthers.length - 3} 件</div> : null}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="btn small secondary home-map-pop-detail-btn"
                    onClick={() => {
                      setMapPopup(null);
                      openDetailFromPopup(e.event_date);
                    }}
                  >
                    この日の詳細を見る
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );

  const listMonthPicker = (
    <label className="home-list-month-picker">
      <span>予定一覧の月</span>
      <select
        className="input home-list-month-select"
        value={month}
        onChange={(e) => {
          const next = e.target.value;
          setMonth(next);
          setDayPopup(null);
          setMapPopup(null);
          if (next !== ALL_MONTHS_VALUE) setMonthScrollIntent(next);
        }}
      >
        <option value={ALL_MONTHS_VALUE}>全てを表示</option>
        {months.map((m) => (
          <option key={m} value={m}>
            {m.replace("-", "年")}月
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <>
    <div className="shell home-shell">
      <header className="topbar home-topbar">
        <div className="home-topbar-inner">
          <div className="home-hero-photo-wrap" aria-hidden="true">
            <img
              src="/images/karin-hero-cutout.png"
              alt=""
              className="home-hero-photo"
              width={176}
              height={176}
              decoding="async"
              fetchPriority="high"
            />
          </div>
          <div className="brand">
            <h1>佳林ちゃんに会いに行ってみよう！</h1>
            <p>スマホ向けイベント確認（公開情報のみ）。現場のほか、配信・ラジオ・テレビなど会場に行かない予定も一覧に含めます。</p>
          </div>
        </div>
      </header>

      <section className="card home-schedule-card">
        {bundle?.generatedAt ? (
          <p className="mono home-generated-at">
            データ更新: {new Date(bundle.generatedAt).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" })}
          </p>
        ) : null}

        <div className="home-top-submit-card">
          <button type="button" className="btn home-top-submit-btn" onClick={() => scrollToSubmitForm()}>
            こんなイベントもあるよ！
          </button>
          <span>
            公式URLがある他グループ・他出演者の予定を投稿できます。
            <strong className="home-submit-delay-inline">カレンダーの反映には時間がかかることがあります。</strong>
          </span>
        </div>

        <HomeNoticeBlocks notices={notices} />
        <div className="home-cal-block cal">
          <div className="home-cal-anchor" id="home-public-month-calendar">
            <div className="home-cal-title-row">
              <h2 className="home-cal-title">カレンダー（日付でプレビュー）</h2>
              <span className="mono home-cal-month-tag">{month === ALL_MONTHS_VALUE ? `全て表示中 / カレンダー ${calendarMonth}` : month}</span>
            </div>
            <div className="home-month-row home-cal-month-row">
              <button
                type="button"
                className="btn small secondary home-month-nav"
                onClick={() =>
                  setMonth((m) => {
                    const next = shiftYearMonth(m === ALL_MONTHS_VALUE ? calendarMonth : m, -1);
                    setMonthScrollIntent(next);
                    return next;
                  })
                }
                aria-label="前の月"
              >
                ‹
              </button>
              <div className="home-month-label">
                <label className="home-month-select-wrap">
                  <span className="home-month-select-label">カレンダーの月</span>
                  <select
                    className="input home-month-select"
                    value={month}
                    onChange={(e) => {
                      const next = e.target.value;
                      setMonth(next);
                      if (next !== ALL_MONTHS_VALUE) setMonthScrollIntent(next);
                    }}
                  >
                    <option value={ALL_MONTHS_VALUE}>全てを表示</option>
                    {months.map((m) => (
                      <option key={m} value={m}>
                        {m.replace("-", "年")}月
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <button
                type="button"
                className="btn small secondary home-month-nav"
                onClick={() =>
                  setMonth((m) => {
                    const next = shiftYearMonth(m === ALL_MONTHS_VALUE ? calendarMonth : m, 1);
                    setMonthScrollIntent(next);
                    return next;
                  })
                }
                aria-label="次の月"
              >
                ›
              </button>
            </div>
            <p className="home-cal-hint">
              <strong>その日のマス（枠のどこでも）</strong>をタップすると当日の予定プレビューが開きます（もう一度同じ日で閉じます）。「詳細を見る」で<strong>下の当日予定一覧</strong>へ移動します。
              {selectedDate ? (
                <>
                  {" "}
                  <button type="button" className="home-inline-link" onClick={() => setSelectedDate(null)}>
                    表示日を解除
                  </button>
                </>
              ) : null}
            </p>
            <p className="home-cal-legend">
              印の見方：
              <KarinCalendarStampLegend isRemote={false} />
              ＝直接会える・現場 ／
              <KarinCalendarStampLegend isRemote />
              ＝オンライン・配信など ／色付き丸＝佳林予定がある日の他グループ・M-line 所属（同じタイトルの複数枠は佳林印1つ）
            </p>
            <div className="cal-head">
              {["日", "月", "火", "水", "木", "金", "土"].map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>
            <div className="cal-grid home-cal-grid">
            {cal.map((cell, idx) => {
              if (!cell.inMonth || !cell.date) {
                return <div key={idx} className="day-cell muted" />;
              }
              const dayKey = cell.date;
              const dayData = bundle?.byDay?.[dayKey];
              const karin = (dayData?.karin ?? []).filter((e) => matchesKarinSubtype(e, karinFilter)).filter((e) => matchesHomeSearch(e, q));
              const karinStamps = karinStampGroupsForDay(karin);
              const others = (dayData?.others ?? []).filter((e) => matchesHomeSearch(e, q));
              const isSel = selectedDate === dayKey;
              const isToday = dayKey === jstTodayYmd();
              return (
                <div
                  key={dayKey}
                  data-cal-day={dayKey}
                  role="button"
                  tabIndex={0}
                  aria-label={`${dayKey}の予定をポップアップで見る`}
                  className={`day-cell day-cell--cal-hit${isSel ? " day-cell--selected" : ""}${isToday ? " day-cell--today" : ""}`}
                  onClick={(ev) => toggleCalendarDayPopup(dayKey, ev.currentTarget as HTMLElement)}
                  onKeyDown={(ev) => {
                    if (ev.key !== "Enter" && ev.key !== " ") return;
                    ev.preventDefault();
                    toggleCalendarDayPopup(dayKey, ev.currentTarget as HTMLElement);
                  }}
                >
                  <div className="day-cell-day-num-row">
                    <span className="day-num">{Number(dayKey.slice(-2))}</span>
                    {isToday ? <span className="day-today-label">今日</span> : null}
                  </div>
                  <div className="stamps">
                    {karinStamps.map(({ titleKey, isRemote, icon }) => (
                      <span
                        key={`${dayKey}-karin-${titleKey}`}
                        className="stamp-cal-hit stamp-karin-wrap"
                        aria-hidden
                      >
                        <KarinCalendarStamp
                          icon={icon}
                          isRemote={isRemote}
                          title={icon ? `宮本佳林（${icon}）` : isRemote ? "宮本佳林（オンライン・配信など）" : "宮本佳林（直接会える・現場）"}
                        />
                      </span>
                    ))}
                    {others.slice(0, 4).map((o, oi) => (
                      <span
                        key={String(o.id || `${dayKey}-cal-other-${oi}`)}
                        className="cal-other-stamp-btn"
                        title={`${o.event_name}（${o.venue ?? "会場"}）`}
                        aria-hidden
                      >
                        <EventBadgeStamp badge={getPublicEventBadge(o)} size="sm" title={`${o.event_name}（${o.venue ?? "会場"}）`} />
                      </span>
                    ))}
                    {others.length > 4 ? (
                      <span className="cal-other-more-btn" aria-hidden>
                        <span style={{ fontSize: 10, color: "var(--muted)" }}>+</span>
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>

        <section className="home-map-block" aria-label="日本地図で見る予定">
          <div className="home-map-head">
            <h2 className="home-map-title">地図で見る予定</h2>
            <label className="home-map-month-picker">
              <span>月別表示</span>
              <select
                className="input home-map-month-select"
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value);
                  setMapPopup(null);
                }}
              >
                <option value={ALL_MONTHS_VALUE}>全てを表示</option>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {m.replace("-", "年")}月
                  </option>
                ))}
              </select>
            </label>
            <span className="mono home-map-caption">{month === ALL_MONTHS_VALUE ? "全ての月" : month}・顔を押すと予定を表示</span>
          </div>
          <p className="home-map-note">地図上の位置は都道府県単位の目安です。実際の会場位置とは少しずれることがあります。</p>
          <div className="home-japan-map">
            <img className="home-japan-map-img" src="/images/japan-dot-map.png?v=1" alt="" loading="lazy" decoding="async" />
            {karinMapGroups.length === 0 ? (
              <p className="home-map-empty">地図に出せる会場予定はまだありません</p>
            ) : null}
            {karinMapGroups.map((g) => {
              const hasRemote = g.events.some((e) => isOnlineKarinStampEvent(e));
              const icon = g.events.find((e) => e.karin_icon?.trim())?.karin_icon ?? null;
              return (
                <button
                  key={g.prefecture}
                  type="button"
                  className="home-map-face"
                  style={{ left: `${g.pos.x}%`, top: `${g.pos.y}%` }}
                  aria-label={`${g.prefecture}の予定 ${g.events.length}件を見る`}
                  onClick={(ev) => toggleMapPrefecturePopup(g.prefecture, ev.currentTarget)}
                >
                  <KarinCalendarStamp icon={icon} isRemote={hasRemote} title={`${g.prefecture}: ${g.events.length}件`} />
                  <span className="home-map-face-count">{g.events.length}</span>
                </button>
              );
            })}
          </div>
        </section>

        <div className="row home-chip-label-row">
          <div className="home-chip-label">宮本佳林の予定タイプ</div>
        </div>
        <div className="row home-chips">
          {(
            [
              ["all", "すべて"],
              ["mini_live", "ミニライブ＆一言"],
              ["withlive", "WithLIVE"],
              ["limista", "リミスタ"],
              ["other", "その他"],
            ] as const
          ).map(([k, label]) => (
            <button key={k} type="button" className={`chip ${karinFilter === k ? "active" : ""}`} onClick={() => setKarinFilter(k)}>
              {label}
            </button>
          ))}
        </div>

        {selectedDate ? (
          <div id={`home-day-detail-${selectedDate}`} className="home-day-detail-anchor">
            <div className="home-schedule-pane home-schedule-pane--karin">
              <div className="home-list-toolbar">
                <div className="home-list-toolbar-text">
                  <span className="home-list-title">宮本佳林の予定一覧</span>
                  <span className="mono home-list-caption">
                    {listCaption}
                    <span className="home-list-caption-focus"> ・ {selectedDate}（{weekdayJp(selectedDate)}）を表示中</span>
                  </span>
                  <span className="home-list-order-hint">（当日に近い順＝日付の早い順・他の日も一覧に残します）</span>
                </div>
              {listMonthPicker}
              </div>
              {loading && <p className="home-loading-line">読み込み中…</p>}
              {err && <p className="home-error-line">{err}</p>}
              <div className="home-schedule-scroll">
                <div className="list home-list-inner">
                  {selectedDayKarinCount === 0 && selectedDayOthers.length > 0 && !loading ? (
                    <p className="home-day-focus-hint card" style={{ marginBottom: 10, padding: "10px 12px", fontSize: 13 }}>
                      {selectedDate}（{weekdayJp(selectedDate)}）はこの条件の<strong>宮本佳林の予定がありません</strong>。他の現場はカレンダーの色付き丸から確認できます。
                    </p>
                  ) : null}
                  {karinCount === 0 && !loading ? (
                    <div className="card home-empty-card">この条件の予定はまだないみたいです</div>
                  ) : null}
                  {renderKarinDayGroups()}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="home-list-toolbar">
              <div className="home-list-toolbar-text">
                <span className="home-list-title">宮本佳林の予定一覧</span>
                <span className="mono home-list-caption">{listCaption}</span>
                <span className="home-list-order-hint">（当日に近い順＝日付の早い順）</span>
              </div>
              {listMonthPicker}
            </div>
            {loading && <p className="home-loading-line">読み込み中…</p>}
            {err && <p className="home-error-line">{err}</p>}
            <div className="home-schedule-scroll">
              <div className="list home-list-inner">
                {karinCount === 0 && !loading && (
                  <div className="card home-empty-card">この条件の予定はまだないみたいです</div>
                )}
                {renderKarinDayGroups()}
              </div>
            </div>
          </>
        )}
      </section>

      {homeSearchAssistBlock}

      <div id="home-submit-event-form" className="home-submit-anchor">
        <SubmitEventForm initialDate={submitInitialDate} />
      </div>

      <section className="about card home-about">
        <h2 style={{ margin: "0 0 8px", fontSize: 14 }}>このサイトについて</h2>
        <p>
          このサイトは、宮本佳林が「佳林ちゃんに会いに行ってみよう！」と思ってもらえたら嬉しいな〜と思いながら、本人が個人的に作っているイベント確認用サイトです！
        </p>
        <p>通知をオンにすると申し込み忘れ対策や開催日前に準備出来るようになっています！</p>
        <p>
          宮本佳林の出演情報や申込み先を見つけやすくすることを目的に、公式サイトなどで公開されている情報をもとに予定を整理しています。
        </p>
        <p>
          ハロー！プロジェクト、M-line、J.P ROOM等の他グループ・他出演者の予定追加については、みなさんに登録してもらい箱推しのみなさんが少しでも宮本佳林含めいろいろなイベントに足を運びやすくなればいいなぁという気持ちから作った入力欄です！
        </p>
        <p>
          また、サイトが落ちてしまったり、表示が崩れてしまったり、急に変な動きをしてしまうこともあるかもしれません……！宮本佳林本人が、分からないことだらけの中でどうにか1人で頑張って作って運用しているので、あたたかい目で見ていただけると嬉しいです！
        </p>
      </section>
    </div>
    {dayPopup
      ? createPortal(
          <>
            <div className="home-day-pop-backdrop" aria-hidden onClick={() => setDayPopup(null)} />
            <div
              className="home-day-pop home-day-pop--floating"
              role="dialog"
              aria-modal="true"
              aria-labelledby="home-day-popup-title"
              style={computeDayPopupStyle(dayPopup)}
              onClick={(ev) => ev.stopPropagation()}
            >
            <div className="home-day-pop-head">
              <h2 id="home-day-popup-title" className="home-day-pop-title">
                {dayPopup.day}（{weekdayJp(dayPopup.day)}）
              </h2>
              <button type="button" className="btn small secondary" onClick={() => setDayPopup(null)}>
                閉じる
              </button>
            </div>
            {loading && !bundle ? (
              <div className="home-day-pop-body">
                <p className="home-day-modal-empty">月データを読み込み中です…</p>
              </div>
            ) : err && !bundle ? (
              <div className="home-day-pop-body">
                <p className="home-day-modal-empty" style={{ color: "#b00020" }}>
                  読み込みに失敗しました: {err}
                </p>
                <p className="home-day-modal-empty" style={{ marginTop: 8, fontSize: 11 }}>
                  開発中は <span className="mono">pages:dev</span>（8788）を動かし、5173 の Vite はプロキシ経由で開いてください。
                </p>
              </div>
            ) : !bundle ? (
              <div className="home-day-pop-body">
                <p className="home-day-modal-empty">月データがありません。しばらく待ってからもう一度お試しください。</p>
              </div>
            ) : (
              <>
                {popupKarin.length > 0 ? (
                  <p className="home-day-pop-lead">この日の佳林は<strong>{popupKarin.length}公演</strong>です。</p>
                ) : null}
                <div className="home-day-pop-body">
                  <section className="home-day-modal-section">
                    <h3 className="home-day-modal-sub">宮本佳林</h3>
                    {popupKarin.length === 0 ? (
                      <p className="home-day-modal-empty">この日の予定はありません</p>
                    ) : (
                      <ul className="home-day-modal-list">
                        {popupKarin.map((e) => (
                          <li key={e.id} className="home-day-modal-li">
                            <span className="home-day-modal-li-title">{e.event_name}</span>
                            <span className="home-day-pop-venue">{venueDisplayLine(e)}</span>
                            <span className="home-day-pop-times">{slotTimeSummaryLine(e)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                  <section className="home-day-modal-section">
                    <h3 className="home-day-modal-sub">その他の現場</h3>
                    {popupOthers.length === 0 ? (
                      <p className="home-day-modal-empty">なし</p>
                    ) : (
                      <ul className="home-day-modal-list">
                        {popupOthers.map((o, oi) => (
                          <li key={String(o.id || `${dayPopup.day}-pop-other-${oi}`)} className="home-day-modal-li home-day-modal-li--row">
                            <EventBadgeStamp badge={getPublicEventBadge(o)} size="sm" title={`${o.event_name}（${o.venue ?? "会場"}）`} />
                            <div className="home-day-pop-other-text">
                              <span className="home-day-modal-li-title">{o.event_name}</span>
                              <span className="home-day-pop-venue">
                                {(o.fan_group_name || o.fan_artist_name) ? [o.fan_group_name, o.fan_artist_name].filter(Boolean).join("・") : venueDisplayLine(o)}
                              </span>
                              <span className="home-day-pop-times">
                                {o.event_date} / {o.prefecture ?? "都道府県未設定"} / {o.performance_count != null ? `${o.performance_count}公演` : slotTimeSummaryLine(o)}
                              </span>
                              {o.detail_url ? (
                                <a className="home-inline-link" href={o.detail_url} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()}>
                                  情報元URL
                                </a>
                              ) : null}
                              {canDeleteFanPost(o) ? (
                                <button
                                  type="button"
                                  className="btn small secondary home-day-pop-delete-btn"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    void deleteOwnFanPost(o);
                                  }}
                                >
                                  自分の投稿を削除
                                </button>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </div>
                <div className="home-day-pop-actions home-day-pop-actions--stack">
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={() => scrollToSubmitForm(dayPopup.day)}
                  >
                    こんなイベントもあるよ！
                  </button>
                  <button type="button" className="btn" onClick={() => openDetailFromPopup(dayPopup.day)}>
                    詳細を見る
                  </button>
                </div>
              </>
            )}
          </div>
          </>,
          document.body,
        )
      : null}
    {mapPopup
      ? createPortal(
          <>
            <div className="home-day-pop-backdrop home-map-pop-backdrop" aria-hidden onClick={() => setMapPopup(null)} />
            <div
              className="home-day-pop home-map-pop home-map-pop--document"
              role="dialog"
              aria-modal="true"
              aria-labelledby="home-map-popup-title"
              style={computeMapPopupDocumentStyle(mapPopup)}
              onClick={(ev) => ev.stopPropagation()}
            >
              <div className="home-day-pop-head">
                <h2 id="home-map-popup-title" className="home-day-pop-title">
                  {mapPopup.prefecture}の予定
                </h2>
                <button type="button" className="btn small secondary" onClick={() => setMapPopup(null)}>
                  閉じる
                </button>
              </div>
              {renderMapPopupBody()}
            </div>
          </>,
          document.body,
        )
      : null}
    {visitorModalEl}
    </>
  );
}
